// A simple pure javascript audio player
// Copyright 2022 Michael Chaney Consulting Corporation
// This assumes the presence of a certain dom tree:
//
//    <div id="audio-player">
//      <div class="gui interface">
//        <div class="controls">
//          <button class="play" tabindex="1">play</button>
//          <button class="pause" tabindex="1">pause</button>
//        </div>
//        <div class="scrub"></div>
//        <div class="progress">
//          <div class="seek-bar">
//            <div class="play-bar"></div>
//          </div>
//        </div>
//        <div class="volume-speaker"></div>
//        <div class="volume-bar">
//          <div class="volume-bar-value"></div>
//        </div>
//        <div class="time-holder">
//          <div class="current-time"></div>
//        </div>
//        <div class="track-title"><span></span></div>
//      </div>
//    </div>

export class Player {

  current_audio_url = null;
  current_title = null;

  state = null;
  audio = null;
  duration = null;
  all_data_buffered = null;
  current_time = null;
  volume = 1.0;

  player_div = null;

  play_button = null;
  pause_button = null;

  scrub_div = null;
  waveform_holder_div = null;
  waveform_img = null;
  progress_div = null;
  seek_bar_div = null;
  play_bar_div = null;

  volume_bar_div = null;
  volume_bar_value_div = null;

  current_time_div = null;

  track_title_div = null;
  track_title_span = null;

  constructor(player_div_dom_id) {
    // See if there's a player attached to the player div.
    // If so, return it.  Otherwise, create a new one.
    this.player_div = document.getElementById(player_div_dom_id);
    if (this.player_div.audio_player_object) {
      return this.player_div.audio_player_object;
    } else {
      this.player_div.audio_player_object = this;

      // Chop the player up
      this.play_button = this.player_div.querySelector('button.play');
      this.pause_button = this.player_div.querySelector('button.pause');
      this.scrub_div = this.player_div.querySelector('.scrub');
      this.waveform_img = this.player_div.querySelector('img.waveform');
      this.progress_div = this.player_div.querySelector('.progress');
      this.seek_bar_div = this.player_div.querySelector('.seek-bar');
      this.play_bar_div = this.player_div.querySelector('.play-bar');
      this.volume_bar_div = this.player_div.querySelector('.volume-bar');
      this.volume_bar_value_div = this.player_div.querySelector('.volume-bar-value');
      this.current_time_div = this.player_div.querySelector('.current-time');
      this.track_title_div = this.player_div.querySelector('.track-title');
      this.track_title_span = this.player_div.querySelector('.track-title span');

      // Note - using arrow function so "this" isn't redefined
      this.progress_div.addEventListener('click', (ev) => {
        if (this.state == 'playing') {
          let offset = ev.offsetX / this.progress_div.offsetWidth;
          this.audio.currentTime = this.duration * offset;
        }
      });

      if (this.volume_bar_div) {
        this.volume_bar_div.addEventListener('click', (ev) => {
          this.volume = ev.offsetX / this.volume_bar_div.offsetWidth;
          this.audio.volume = this.volume;
          this.set_volume_bar(this.volume);
        });
      }

      this.play_button.addEventListener('click', (ev) => {
        this.continue_playing();
      });

      this.pause_button.addEventListener('click', (ev) => {
        this.pause();
      });

      // https://stackoverflow.com/questions/29703473/pure-javascript-draggable-element
      if (this.scrub_div) {
        this.scrub_div.addEventListener('mousedown', (ev) => {
          // only allow scrub if playing or paused
          if (this.state != 'playing' && this.state != 'paused') return;

          const scrub_div = this.scrub_div;
          let player_object = this;
          const initial_state = this.state;

          const initial_x = scrub_div.offsetLeft;
          const initial_y = scrub_div.offsetTop;
          const mouse_x = ev.clientX;
          const mouse_y = ev.clientY;
          const min_x = this.progress_div.offsetLeft;
          const max_x = this.progress_div.offsetLeft + this.progress_div.offsetWidth;

          this.state = 'scrub';
          this.audio.pause();

          window.addEventListener('mousemove', move_scrub, false);
          window.addEventListener('mouseup', finish_scrub, false);

          function new_x(client_x) {
            let new_left = initial_x + client_x - mouse_x;
            if (new_left < min_x) new_left = min_x;
            if (new_left > max_x) new_left = max_x;
            return new_left;
          }

          // returns 0 to 1 offset
          function offset_at(x) {
            return x / player_object.progress_div.offsetWidth;
          }

          function time_at(offset) {
            return player_object.duration * offset;
          }

          function move_scrub(ev2) {
            const new_left = new_x(ev2.clientX);
            scrub_div.style.left = `${new_left}px`;
            player_object.set_current_time_display(time_at(offset_at(new_left - min_x)));
          }

          function finish_scrub(ev2) {
            window.removeEventListener('mousemove', move_scrub, false);
            window.removeEventListener('mouseup', finish_scrub, false);
            player_object.state = initial_state;
            move_scrub(ev2);
            const offset = offset_at(new_x(ev2.clientX) - min_x);
            player_object.audio.currentTime = time_at(offset);
            if (player_object.state == 'playing') {
              player_object.audio.play();
            }
          }
        });
      }
    }
  }

  play_or_pause(audio_url, title, waveform_url = null) {
    if (this.current_audio_url) {
      if (this.current_audio_url === audio_url) {
        this.pause_or_continue_playing();
      } else {
        this.stop_playing();
        this.start_playing(audio_url, title, waveform_url);
      }
    } else {
      this.start_playing(audio_url, title, waveform_url);
    }
  }

  continue_playing() {
    if (this.state == 'paused' || this.state == 'ended') {
      this.state = 'playing';
      this.player_div.classList.add('playing');
      this.player_div.classList.remove('paused');
      this.audio.play();
    }
  }

  pause() {
    if (this.state == 'playing') {
      this.state = 'paused';
      this.player_div.classList.add('paused');
      this.audio.pause();
    }
  }

  pause_or_continue_playing() {
    if (this.state == 'playing') {
      this.pause();
    } else if (this.state == 'paused') {
      this.continue_playing();
    } else {
      // no idea
    }
  }

  stop_playing() {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0.0;
      this.audio = null;
    }
    this.player_div.classList.remove('playing');
    this.player_div.classList.remove('paused');
    this.current_audio_url = null;
    this.state = null;
  }

  start_playing(audio_url, title, waveform_url = null) {
    this.player_div.classList.add('playing');
    this.player_div.classList.remove('paused');
    this.current_audio_url = audio_url;
    this.state = 'playing';
    this.set_current_time_display('0:00');
    this.set_title(title);
    this.set_waveform(waveform_url);
    this.audio = new Audio(audio_url);
    this.audio.volume = this.volume;
    this.audio.addEventListener('play', () => {
    });
    this.audio.addEventListener('playing', () => {
    });
    this.audio.addEventListener('pause', () => {
    });
    this.audio.addEventListener('durationchange', () => {
      this.duration = this.audio.duration;
    });
    this.audio.addEventListener('timeupdate', () => {
      if (this.audio && this.audio.currentTime) {
        this.set_play_bar_width(this.audio.currentTime);
        this.set_scrub_position(this.audio.currentTime);
        this.set_current_time_display(this.audio.currentTime);
      }
    });
    this.audio.addEventListener('progress', () => {
      this.set_seek_bar_width();
    });
    this.audio.addEventListener('ended', () => {
      this.state = 'ended';
      this.player_div.classList.remove('playing');
      this.player_div.classList.remove('paused');
      this.audio.currentTime = 0;
      this.set_play_bar_width(this.audio.currentTime);
      this.set_scrub_position(this.audio.currentTime);
      this.set_current_time_display(this.audio.currentTime);
    });

    this.audio.play();
  }

  // position is 0.0 to 1.0
  scrub(position) {
    if (this.audio.duration) {
      const new_time = position * this.audio.duration;
      let seekable = this.audio.seekable;
      let in_range = false;
      for (let i=0; i<seekable.length; i++) {
        if (seekable.start(i) < new_time && seekable.end(i) > new_time) {
          in_range = true;
          break;
        }
      }
      if (in_range) {
        this.audio_player.currentTime = new_time;
      }
    }
  }

  set_play_bar_width(time) {
    const width = 100.0 * time / this.duration;
    this.play_bar_div.style.width = `${width}%`;
  }

  set_scrub_position(time) {
    if (this.scrub_div) {
      const offset = Math.round(this.progress_div.offsetLeft + this.progress_div.offsetWidth * time / this.duration);
      this.scrub_div.style.left = `${offset}px`;
    }
  }

  set_seek_bar_width() {
    if (this.duration) {
      // assuming that the highest time range end is the end
      var seekable = this.audio.seekable;
      var last_end = null;
      if (seekable.length > 0) {
        last_end = seekable.end(seekable.length-1);
        this.all_data_buffered = (last_end >= this.duration);
      }
      // Now, set the width of the seek bar based on this
      const width = (last_end / this.duration) * 100.0;
      this.seek_bar_div.style.width = `${width}%`;
    }
  }

  set_volume_bar(volume) {
    this.volume_bar_value_div.style.width = `${volume * 100.0}%`;
  }

  set_current_time_display(time) {
    this.current_time_div.textContent = Player._time_to_minutes_and_seconds(time);
  }

  set_title(title) {
    if (this.track_title_div) {
      this.track_title_span.textContent = title;
      let player_object = this;
      setTimeout(set_title_animation,0);
      function set_title_animation() {
        if (player_object.track_title_span.offsetWidth > player_object.track_title_div.offsetWidth) {
          player_object.track_title_span.classList.add('animated');
        } else {
          player_object.track_title_span.classList.remove('animated');
        }
      }
    }
  }

  set_waveform(waveform_url) {
    if (this.waveform_img && waveform_url) {
      this.waveform_img.src = waveform_url;
    }
  }

  static _time_to_minutes_and_seconds(time) {
    if (!time || isNaN(time)) time = 0.0;
    var minutes = Math.floor(time / 60.0);
    var seconds = ('00' + Math.round(time % 60)).substr(-2);
    return `${minutes}:${seconds}`;
  }
}
